/**
 * Template Preview Dialog Screenshot Capture Utility
 *
 * Captures screenshots from the PreviewDialog iframe containing
 * compiled MJML email templates.
 *
 * Usage:
 * 1. Open PreviewDialog with template content
 * 2. Wait for render (handled internally)
 * 3. Call capturePreviewDialogScreenshot()
 * 4. Returns WebP blob ready for upload
 */

import html2canvas from 'html2canvas';

/**
 * Capture screenshot from PreviewDialog iframe
 *
 * This function looks for the email preview iframe in the DOM,
 * accesses its internal document, and captures the rendered content.
 *
 * @param waitTime - Time to wait for iframe render in milliseconds (default 1500ms)
 * @returns Promise<Blob> - Screenshot as WebP blob
 * @throws Error if iframe not found or capture fails
 */
export const capturePreviewDialogScreenshot = async (
  waitTime: number = 1500
): Promise<Blob> => {
  try {
    console.log('[capturePreviewDialog] Starting capture process...');

    // Wait for iframe to fully render
    if (waitTime > 0) {
      console.log('[capturePreviewDialog] Waiting', waitTime, 'ms for iframe to render...');
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    // Find the preview iframe in the DOM
    const iframe = document.querySelector(
      'iframe[title="Email Preview"]'
    ) as HTMLIFrameElement;

    if (!iframe) {
      throw new Error(
        'Preview iframe not found. Make sure PreviewDialog is open and rendered.'
      );
    }

    console.log('[capturePreviewDialog] Iframe found, dimensions:', {
      width: iframe.offsetWidth,
      height: iframe.offsetHeight,
      src: iframe.src?.substring(0, 50) + '...'
    });

    // Access iframe's internal document
    // This is the key difference - we need to capture the iframe's content, not the iframe element itself
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDocument) {
      throw new Error(
        'Cannot access iframe content. The iframe may be cross-origin or not yet loaded.'
      );
    }

    // Get the body element from the iframe
    const iframeBody = iframeDocument.body;

    if (!iframeBody) {
      throw new Error('Iframe body not found. Content may not be loaded yet.');
    }

    console.log('[capturePreviewDialog] Iframe document accessible, body dimensions:', {
      scrollWidth: iframeBody.scrollWidth,
      scrollHeight: iframeBody.scrollHeight,
      clientWidth: iframeBody.clientWidth,
      clientHeight: iframeBody.clientHeight
    });

    // Additional wait for images and fonts to load
    console.log('[capturePreviewDialog] Waiting for images and fonts to load...');
    await new Promise(resolve => setTimeout(resolve, 500));

    // Calculate capture dimensions
    // We want a preview card-sized capture, not the entire long email
    const targetWidth = iframeBody.scrollWidth || 600;
    const targetHeight = Math.min(iframeBody.scrollHeight, 800); // Cap at 800px height for preview

    console.log('[capturePreviewDialog] Capture dimensions:', {
      targetWidth,
      targetHeight,
      fullHeight: iframeBody.scrollHeight
    });

    // Capture the iframe's body content with higher resolution
    const canvas = await html2canvas(iframeBody, {
      backgroundColor: '#ffffff',
      scale: 3,  // 3x resolution for crisp preview images
      logging: false,  // Disable verbose logging (re-enable if debugging)
      useCORS: true,  // Handle external images
      allowTaint: true,  // Allow cross-origin content
      // Capture full width but limit height to preview size
      windowWidth: targetWidth,
      windowHeight: targetHeight,
      width: targetWidth,
      height: targetHeight,
      x: 0,  // Start from left edge
      y: 0,  // Start from top (captures "above the fold" content)
      // Important: use the iframe's window for fonts and styles
      foreignObjectRendering: false,
      imageTimeout: 15000,  // 15 second timeout for images
      onclone: (clonedDoc) => {
        console.log('[capturePreviewDialog] Document cloned for rendering, height capped at:', targetHeight);
      }
    });

    console.log('[capturePreviewDialog] Canvas created, size:', {
      width: canvas.width,
      height: canvas.height
    });

    // Verify canvas has content (not blank)
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hasContent = imageData.data.some((value, index) => {
        // Check if there are non-white pixels (skip alpha channel)
        if (index % 4 === 3) return false; // Skip alpha
        return value !== 255;
      });

      if (!hasContent) {
        console.warn('[capturePreviewDialog] Canvas appears to be blank (all white pixels)');
        throw new Error('Captured screenshot is blank. The iframe content may not have loaded properly.');
      }
    }

    // Convert canvas to WebP blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            console.log('[capturePreviewDialog] Capture successful, size:',
              (result.size / 1024).toFixed(2), 'KB'
            );
            resolve(result);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/webp',
        0.85  // 85% quality - good balance between quality and size
      );
    });

    return blob;

  } catch (error) {
    console.error('[capturePreviewDialog] Capture failed:', error);
    throw new Error(
      `Screenshot capture failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Helper to check if PreviewDialog is currently open
 *
 * Useful for conditional logic before attempting capture
 *
 * @returns boolean - True if preview iframe exists in DOM
 */
export const isPreviewDialogOpen = (): boolean => {
  return !!document.querySelector('iframe[title="Email Preview"]');
};

/**
 * Helper to wait for PreviewDialog to open
 *
 * Polls the DOM for the preview iframe with timeout
 *
 * @param timeout - Maximum time to wait in milliseconds (default 5000ms)
 * @param interval - Poll interval in milliseconds (default 100ms)
 * @returns Promise<void> - Resolves when dialog is open, rejects on timeout
 */
export const waitForPreviewDialog = async (
  timeout: number = 5000,
  interval: number = 100
): Promise<void> => {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(() => {
      if (isPreviewDialogOpen()) {
        clearInterval(checkInterval);
        console.log('[capturePreviewDialog] Preview dialog detected');
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for PreviewDialog to open'));
      }
    }, interval);
  });
};
